"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { fetchCurrentUser, loginV2, logoutRequest } from "@/shared/auth/api/auth";
import {
  clearStoredAuthSession,
  formatAuthUserName,
  getStoredAuthSession,
  setStoredAuthSession,
  type AuthSession,
} from "@/shared/auth/session";

type LoginPayload = {
  phone: string;
  password: string;
};

type AuthContextValue = {
  session: AuthSession | null;
  user: AuthSession["user"] | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  login: (payload: LoginPayload) => Promise<AuthSession>;
  isLoginPending: boolean;
  loginError: string | null;
  resetLoginError: () => void;
  applySession: (session: AuthSession) => void;
  updateUser: (patch: Partial<AuthSession["user"]>) => void;
  logout: () => void;
  displayName: string;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const authRevisionRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    const revision = authRevisionRef.current;
    const cached = getStoredAuthSession();
    if (cached) {
      setSession(cached);
    }

    fetchCurrentUser()
      .then((user) => {
        if (!isMounted || revision !== authRevisionRef.current) return;
        const nextSession = { user };
        setStoredAuthSession(nextSession);
        setSession(nextSession);
      })
      .catch(() => {
        if (!isMounted || revision !== authRevisionRef.current) return;
        clearStoredAuthSession();
        setSession(null);
      })
      .finally(() => {
        if (isMounted) {
          setIsHydrated(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const applySession = useCallback((nextSession: AuthSession) => {
    authRevisionRef.current += 1;
    setStoredAuthSession(nextSession);
    setSession(nextSession);
  }, []);

  const updateUser = useCallback((patch: Partial<AuthSession["user"]>) => {
    setSession((current) => {
      if (!current) {
        return current;
      }
      const next: AuthSession = { ...current, user: { ...current.user, ...patch } };
      setStoredAuthSession(next);
      return next;
    });
  }, []);

  const loginMutation = useMutation({
    mutationFn: loginV2,
    onSuccess: applySession,
  });

  const logout = useCallback(() => {
    authRevisionRef.current += 1;
    logoutRequest().catch(() => {
      // Ignore network/auth errors during logout — local cleanup is what matters.
    });
    clearStoredAuthSession();
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      isAuthenticated: Boolean(session?.user?.id),
      isHydrated,
      login: loginMutation.mutateAsync,
      isLoginPending: loginMutation.isPending,
      loginError: loginMutation.error instanceof Error ? loginMutation.error.message : null,
      resetLoginError: loginMutation.reset,
      applySession,
      updateUser,
      logout,
      displayName: formatAuthUserName(session?.user),
    }),
    [applySession, isHydrated, loginMutation, logout, session, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
